/**
 * Workspace persistence mode: backend REST vs device-local snapshot vs
 * sync-engine (local SQLite + background push/pull).
 */
import { syncEngineEnabled } from './featureFlags';

export function isGitWorkspace(workspace) {
  return workspace?.storage === 'git' && Boolean(workspace?.rootPath);
}

export function isLocalWorkspace(workspace, isAuthenticated) {
  if (!workspace) return false;
  if (!isAuthenticated) {
    return (
      workspace.storage === 'local' ||
      workspace.storage === 'git' ||
      workspace.storage == null
    );
  }
  return workspace.storage === 'local' || workspace.storage === 'git';
}

/** True when the current workspace syncs collections/requests/variables via the API. */
export function isBackendWorkspace(workspace, isAuthenticated) {
  return Boolean(isAuthenticated && workspace && workspace.storage !== 'local');
}

/**
 * Sync-engine (Phase 3) mode helpers. An ENROLLED cloud workspace keeps its
 * server identity (sharing/publish/members stay on REST via
 * isBackendWorkspace) but its DATA persistence flows through the local
 * SQLite store + background engine instead of synchronous REST.
 */
export function isSyncEngineWorkspace(workspace, isAuthenticated) {
  return isBackendWorkspace(workspace, isAuthenticated) && syncEngineEnabled(workspace);
}

/** Cloud workspace still on the legacy synchronous-REST data path. */
export function isCloudRestWorkspace(workspace, isAuthenticated) {
  return isBackendWorkspace(workspace, isAuthenticated) && !syncEngineEnabled(workspace);
}

/** Data persistence goes through the local store (local, git, or enrolled cloud). */
export function usesLocalDataPath(workspace, isAuthenticated) {
  return (
    isLocalWorkspace(workspace, isAuthenticated) ||
    isSyncEngineWorkspace(workspace, isAuthenticated)
  );
}

export function generateLocalWorkspaceId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Minimal entries stored in manifest for rebuilding after login. */
export function buildLocalManifestEntries(workspaces) {
  return (workspaces || [])
    .filter(
      (w) =>
        w &&
        (w.storage === 'local' || w.storage === 'git' || w.is_local === true)
    )
    .map((w) => ({
      id: w.id,
      local_uid: w.local_uid ?? w.id,
      name: w.name,
      description: w.description || '',
      type: 'personal',
      storage: w.storage === 'git' ? 'git' : 'local',
      rootPath: w.rootPath || undefined,
      git: w.git || undefined,
      source: 'personal',
      role: 'owner',
    }));
}

/** Dedupe by id: remote API workspaces plus manifest/local-only entries. */
export function mergeRemoteAndLocalWorkspaces(remoteList, localsFromManifest) {
  const map = new Map();
  const add = (w) => {
    if (w != null && w.id !== undefined && w.id !== null && String(w.id) !== '') {
      map.set(String(w.id), w);
    }
  };
  (remoteList || []).forEach(add);
  (localsFromManifest || []).forEach((meta) => {
    if (!meta || meta.id == null) return;
    const idStr = String(meta.id);
    if (!map.has(idStr)) {
      add({
        ...meta,
        storage: 'local',
        type: meta.type || 'personal',
        source: meta.source || 'personal',
        role: meta.role || 'owner',
      });
    }
  });
  return [...map.values()];
}
